import { useNetwork } from '@/src/contexts';
import { getOfferSide } from '@/src/helpers';
import { useExecuteFireblocksTransaction, useSelectedTokens } from '@/src/hooks';
import { TokenCode, TokenId, TransactionType } from '@/src/models';
import { TableCell, TableRow } from '@/src/ui-components';
import { VaultAccount } from '@/src/hooks/apis/fireblocks/fireblocks.types';
import { Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { AccountOffer } from 'xrpl';
import { useAccountOfferValues } from '../hooks';

type OpenOrderProps = {
  offer: AccountOffer;
  baseTokenId: TokenId;
  quoteTokenId: TokenId;
  vaultAccount: VaultAccount;
};

/**
 * Component to display an open order for the selected token pair
 * @param offer AccountOffer object
 * @param baseTokenId Base token ID
 * @param quoteTokenId Quote token ID
 * @returns React.ReactNode
 */
export const OpenOrder = ({ offer, baseTokenId, quoteTokenId, vaultAccount }: OpenOrderProps) => {
  const [baseToken, quoteToken] = useSelectedTokens(baseTokenId, quoteTokenId);

  const offerType = getOfferSide(
    offer,
    baseToken?.code as TokenCode,
    quoteToken?.code as TokenCode
  );
  const { amount, total, price } = useAccountOfferValues({ offer, offerType });

  const { isTestnet } = useNetwork();
  const { mutateAsync: executeFireblocksTransaction } = useExecuteFireblocksTransaction();

  const handleCancelOffer = async () => {
    if (!vaultAccount?.id) {
      toast.error('Vault Account ID not found');
      return;
    }

    const loadingToastId = toast.loading(
      `Cancelling ${offerType} order with sequence ${offer.seq}...`
    );

    try {
      await executeFireblocksTransaction({
        transactionType: TransactionType.OFFER_CANCEL,
        assetId: isTestnet ? 'XRP_TEST' : 'XRP',
        vaultAccountId: vaultAccount.id,
        params: {
          offerSequence: offer.seq,
        },
      });

      toast.dismiss(loadingToastId);
      toast.success(`${offerType} order with sequence ${offer.seq} cancelled successfully`);
    } catch (error) {
      const description =
        error instanceof Error
          ? error.message
          : 'Please try again or contact support if the issue persists';

      toast.dismiss(loadingToastId);
      toast.error(`Failed to cancel ${offerType} order with sequence ${offer.seq}`, {
        description,
      });
    }
  };

  return (
    <TableRow>
      <TableCell className="capitalize p-2">{offerType}</TableCell>
      <TableCell className="p-2 font-mono">{price}</TableCell>
      <TableCell className="p-2 font-mono">{amount}</TableCell>
      <TableCell className="p-2 font-mono">{total}</TableCell>
      <TableCell className="p-2 font-mono">{offer.seq}</TableCell>
      <TableCell className="p-2">
        <Trash2
          className="mx-auto cursor-pointer"
          size={16}
          color="red"
          onClick={handleCancelOffer}
        />
      </TableCell>
    </TableRow>
  );
};
